// @ts-check

/**
 * @typedef {import('./coreTypes').ResultAtom} ResultAtom
 * @typedef {import('./coreTypes').JSResultAtom} JSResultAtom
 * @typedef {import('./coreTypes').QuerySpec} QuerySpec
 * @typedef {import('./coreTypes').CampaignSplashInfo} CampaignSplashInfo
 * @typedef {import('./coreTypes').JSX} JSX
 */

// @ts-ignore
import luLogo from "../images/LU.svg";
// @ts-ignore
import drwText from "../images/donor_recognition_wall.svg";
// @ts-ignore
import luSeal from "../images/Seal.svg";
// @ts-ignore
import tabletIcon from "../images/tablet_icon.svg";
// @ts-ignore
import arrowUnderTabletIcon from "../images/arrow_under_tablet_icon.svg";



import React, {useEffect, useState, useCallback, useRef} from "react";
import "../styles/App.css";
import "../styles/WebLoadingText.css";

import DonorCardLayout from "./DonorCardLayout";
import parseDonors from "./ResultAtomParsingFunctions";
import processQuerySpec from "./processQuerySpec";
import DEBUG from "./Debugging";
import { generateResponseBackUrl, generateSocketUrl } from "./urls";
import { pushDonorListIsPopulated } from "./AnalyticsFunctions";


import CampaignList, { getQueryName } from './CampaignList';
import CampaignInfoScreen from "./CampaignInfoScreen";

import NamedBuildingScreenLayout from "./NamedBuildingScreenLayout";
import NamedBuildingList from './NamedBuildingList';

import ResultsLoadingManagerTVInstance from './ResultsLoadingManagerTVInstance.js';


/**
 * @param {string} msg 
 * @returns {true|string}
 */
function messageIsProperlyFormatted(msg) {
	if (!Array.isArray(msg)) return 'outermost container not array';
	if (msg.length !== 2) return 'outermost array not length 2';
	switch (msg[0]) {
		case "display":
			if (!Array.isArray(msg[1])) return 'display payload must be array';
			if (msg[1].length > 0) {
				for (let a = 0; a < msg[1].length; a++) {
					if (!Array.isArray(msg[1][a])) return 'display payload array contents must be arrays';
					if (msg[1][a].length !== 2) return 'display payload array content arrays must be length 2';
					if (typeof msg[1][a][0] !== 'string') return 'display payload array contents must be list of [tag, payload]';
				}
			}
			break;
		case "change_index_to":
			if (typeof msg[1] !== 'number') return 'change_index_to payload must be a number';
			break;
		default: return 'outermost tag is unknown';
	}
	return true;
}


function messageIsNamedBuildings(msg) {
	try {
		return (
			(msg[1][0][0] === 'campaign_name' 
				|| msg[1][0][0] === 'campaign_search_string')
				&& msg[1][0][1] === 'Named Buildings');
	} catch (e) { return false; }
}

/**
 * @param {string} msg 
 * @returns {string|null}
 */
function getNameOfCampaignIfCampaignOrNullIfNot(msg) {
	if ((msg[1][0][0] === 'campaign_name' 
			|| msg[1][0][0] === 'campaign_search_string')
			&& msg[1][0][1] !== 'Named Buildings') {
			return msg[1][0][1];
		}
	else return null;
}


const SplashScreen = (props) => {

	/**
	 * @type {[CampaignSplashInfo, React.Dispatch<React.SetStateAction<CampaignSplashInfo>>]}
	 */
	// @ts-ignore
	const [currentCampaignInfo, setCurrentCampaignInfo] = useState(null);


	const ss_SPLASH = 'splash';
	const ss_NAMED_BUILDING = 'named_building';
	const ss_CAMPAIGN_SPLASH = 'campaign_splash';
	const ss_DONOR_CARDS = 'donor_cards';

	const [screen, setScreen] = useState(ss_SPLASH); 


	const venue = (props.venue === null) ? "donor_testing" : props.venue;


	// default/empty -> ""
	// loading -> "loading"
	// tablet notifier -> "tablet notify"
	// unavailable -> "unavailable"

	const [splashState, setSplashState] = useState("tablet notify");
	

	/**
	 * @type {JSResultAtom[][]}
	 */
	const emptyJsResultArrayOfArray = [];
	const [cardsList, setCardsList] = useState(emptyJsResultArrayOfArray);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [pageTotalIsTentative, setPageTotalIsTentative] = useState(true);
	
	/**
	 * @type {React.MutableRefObject<WebSocket | null>}
	 */
    const currentSocket = useRef(null);

	/**
	 * @type {React.MutableRefObject<NodeJS.Timeout | null>}
	 */
	const revertTimer = useRef(null);


	const namedBuildingListTotalPages = Math.ceil(NamedBuildingList.length / 2);

	// eslint-disable-next-line 
	const revertToInitialSetup = () => {
		setScreen(ss_SPLASH);
		setSplashState("tablet notify");
		sendResponseBack(['display_reverted', null]);
	};


	const disableRevertTimer = useCallback(() => {
		if (revertTimer.current !== null) {
			/**
			 * @type {NodeJS.Timeout} timer
			 */
			const timer = revertTimer.current;
			clearTimeout(timer);
			revertTimer.current = null;
		}
	}, [revertTimer]);


	const startRevertTimer = useCallback(() => {
		if (revertTimer.current !== null) {
			disableRevertTimer();
		}
		const tenMinutesInMillis = 1000 * 60 * 10;
		revertTimer.current =
			setTimeout(revertToInitialSetup, tenMinutesInMillis);
	}, [revertTimer, revertToInitialSetup, disableRevertTimer]);



	const sendResponseBackUrl = generateResponseBackUrl(venue);


	const sendResponseBack = useCallback(
		/**
		 * @param {[string, any]} resp 
		 */
		resp => {
			const req = new XMLHttpRequest();
			req.open("POST", sendResponseBackUrl, true);
			req.setRequestHeader("Content-Type", "application/json");
			req.send(JSON.stringify(resp));
			if (DEBUG) {
				console.log("Sent response back: ", resp);
			}
			const noRevertResponses = ['display_reverted', 'results_loading'];
			if (!noRevertResponses.includes(resp[0])) {
				startRevertTimer();
		}
	}, [sendResponseBackUrl, startRevertTimer]);


    const setSocket = useCallback(() => {
        if (venue) {
			
			/**
			 * @type {JSResultAtom[][][]}
			 */
			let fullCardsList = [];
			let localCurrentIndex = 0;
			let localScreen;
			
			const socketUrl = generateSocketUrl(venue);
            const socket = new WebSocket(socketUrl);
			
            currentSocket.current = socket;
            socket.onmessage = ({data}) => {
                const newMessage = JSON.parse(data)

				if (DEBUG) {
					console.log("new message:")
					console.log(newMessage)
				}

				const fmtVerdict = messageIsProperlyFormatted(newMessage);

				if (typeof fmtVerdict === 'string') {
					if (DEBUG) {
						console.log("Message format error: " + fmtVerdict);						
					}
					return;
				}	

				disableRevertTimer();

				if (newMessage[0] === 'display') {
						if (DEBUG) {
							console.log("before processQuerySpec");
						}

						if (messageIsNamedBuildings(newMessage)) {

							ResultsLoadingManagerTVInstance.setActiveLoadingId(
								ResultsLoadingManagerTVInstance.generateLoadingId());

							if (DEBUG) {
								console.log("In Named Buildings section.");
							}

							setCurrentIndex(0);
							setTotalPages(namedBuildingListTotalPages);
							setScreen(ss_NAMED_BUILDING);
							localScreen = ss_NAMED_BUILDING;
						
							sendResponseBack(["results_displayed", {"page_count": namedBuildingListTotalPages, "current_index": 0 }]);
						}	
						else {
							processQuerySpec(
								newMessage[1], 
								paginatedResults => {
									// paginatedResults : pages of columns of (ResultAtom, size) pairs

									if (paginatedResults === null) {
										paginatedResults = [[[]]];
									}

									if (DEBUG) {
										console.log("in processQuerySpec");
										console.log(paginatedResults);
									}

									setCurrentIndex(0);
									localCurrentIndex = 0;

									const donorsParsed = paginatedResults.map(p => p.map(c => parseDonors(c)));
									
									fullCardsList = donorsParsed;
									
									setTotalPages(donorsParsed.length);
									
									if (DEBUG) {
										console.log("fullCardsList: ");
										console.log(fullCardsList);
									}

									if (donorsParsed.length > currentIndex && currentIndex >= 0) {
										setCardsList(donorsParsed[currentIndex]);
									}
									else {
										setCardsList([]);
									}


									const campName = getNameOfCampaignIfCampaignOrNullIfNot(newMessage);	
									
									pushDonorListIsPopulated((campName === null) && (fullCardsList.length > 0));

									let campaignSplashSet = false;

									if (campName !== null) {
										
										const campObj = CampaignList.find(k => getQueryName(k) === campName);

										if (campObj !== undefined) {

											setCurrentCampaignInfo(campObj);
											
											setScreen(ss_CAMPAIGN_SPLASH);
											localScreen = ss_CAMPAIGN_SPLASH;

											campaignSplashSet = true;
										}
										else {
											setScreen(ss_DONOR_CARDS);
											localScreen = ss_DONOR_CARDS;
										}
									}
									else {
										setScreen(ss_DONOR_CARDS);
										localScreen = ss_DONOR_CARDS;
									}

									const campaignSplashSetPreviously = 
										(screen === ss_CAMPAIGN_SPLASH) || (localScreen === ss_CAMPAIGN_SPLASH);

									if (campaignSplashSet || campaignSplashSetPreviously) {
										sendResponseBack(["partial_results_displayed", {"page_count": donorsParsed.length, "current_index": 0, "campaign_splash": true }]);
									} else {
										sendResponseBack(["partial_results_displayed", {"page_count": donorsParsed.length, "current_index": 0 }]);
									}
									
									setPageTotalIsTentative(true);
									return true;
								},
								nextPaginatedResults => {

									let payload = { "page_count": fullCardsList.length, "current_index": localCurrentIndex };

									const campaignSplashSet = 
										(screen === ss_CAMPAIGN_SPLASH) || (localScreen === ss_CAMPAIGN_SPLASH);

									if (campaignSplashSet) { payload = {...payload, "campaign_splash": true }; }


									if (nextPaginatedResults === null) {

										setPageTotalIsTentative(false);
										sendResponseBack(["results_displayed", payload]);

										return false;
									}

									const newDonors = nextPaginatedResults.map(p => p.map(c => parseDonors(c)));

									fullCardsList = fullCardsList.concat(newDonors);
									setTotalPages(fullCardsList.length);

									if (fullCardsList.length > localCurrentIndex && localCurrentIndex >= 0) {
										setCardsList(fullCardsList[localCurrentIndex]);
									}
									else {
										setCardsList([]);
									}

									sendResponseBack(["partial_results_displayed", payload]);
										
									return true;
								},
								m => {
									setScreen(ss_SPLASH);
									localScreen = ss_SPLASH;

									setSplashState("error");
								 });

							setScreen(ss_SPLASH);
							localScreen = ss_SPLASH;
							setSplashState("loading");
							sendResponseBack(["results_loading", null]);
						}
				}
				else if (newMessage[0] === 'change_index_to') {

					setScreen(oldScreen => {

						if (DEBUG) {
							console.log("screen: ", oldScreen);
						}

						if (oldScreen === ss_CAMPAIGN_SPLASH) {
							if (DEBUG) {
								console.log("in campaign splash screen");
							}
							sendResponseBack(["new_index_ack", { "new_index" : currentIndex }]);
							localScreen = ss_DONOR_CARDS;
							return ss_DONOR_CARDS;
						}
						else if (oldScreen === ss_NAMED_BUILDING) {
							if (DEBUG) {
								console.log("in named buildings screen");
							}
							const newIndex = newMessage[1];
							setCurrentIndex(newIndex);
							localCurrentIndex = newIndex;
							sendResponseBack(["new_index_ack", { "new_index" : newIndex }]);
							localScreen = oldScreen;
							return oldScreen;
						}
						else {
							if (DEBUG) {
								console.log("other screen");
							}
							const newIndex = newMessage[1];
						
							setCurrentIndex(newIndex);
							localCurrentIndex = newIndex;
							if (fullCardsList.length > newIndex && newIndex >= 0) {
								setCardsList(fullCardsList[newIndex]);
							}
							else {
								setCardsList([]);
							}
							sendResponseBack(["new_index_ack", { "new_index" : newIndex }]);
							localScreen = ss_DONOR_CARDS;
							return ss_DONOR_CARDS;
						}
					});
				}
            };
            window.addEventListener('beforeunload', (e) => {
                if (currentSocket !== null && currentSocket.current != null) {
                    currentSocket.current.send('close');
                    currentSocket.current.close();
                }
            })
        }
    }, [
		venue, setCardsList, currentIndex, setCurrentIndex, 
		setTotalPages, screen, setScreen, namedBuildingListTotalPages, sendResponseBack, 
		disableRevertTimer]);


    useEffect(() => {
        setSocket();
    }, [setSocket]);

	


	const tabletNotifyView = 
		(<><p>Use the tablet to search donors by name</p>
		<p>or browse by campaign</p>
		<img src={tabletIcon} className="App-tablet-icon" alt="tablet icon"/>
		<img src={arrowUnderTabletIcon} className="App-tablet-icon-arrow" alt="tablet icon arrow"/></>);

	const loadingView = (
		<>
		<div className="web-loading-text__container">
                        <p className="web-loading-text__loading">Loading</p>
                        <p className="web-loading-text__dot-1"> . </p>
                        <p className="web-loading-text__dot-2"> . </p>
                        <p className="web-loading-text__dot-3"> . </p>
                      </div>
		<p style={{ marginTop: 20 }}/>
		<p>"Nothing of eternal significance happens apart from prayer." - Jerry Falwell</p>
		</>);

	const errorView = (
			<p style={errorTextStyle}>There was an error fetching the list of donors.</p>
		);



	let splashView = null;

	switch (splashState) {
		case 'tablet notify': splashView = tabletNotifyView; break;
		case 'loading': splashView = loadingView; break;
		case 'error': splashView = errorView; break;
		default: throw new Error("Invlaid splash screen state");
	}


	/** @type {boolean} */
	const thereAreNoCards = cardsList.flatMap(l => l).length === 0;
	

	switch (screen) {
		case ss_SPLASH:
			return (
				<div className="App">
					<header className="App-header">
						<div id="overlay"/>
						<img src={luSeal} className="App-logo" alt="logo"/>
						<img src={luLogo} className="App-logo-name" alt="Liberty University"/>
						<img src={drwText} className="drw-text-name" alt="Donor Recognition Wall"/>
						<h1>  </h1>
						{splashView}
					</header>
				</div>);

		case ss_NAMED_BUILDING:
			return (
				<div className="top-container-root">
					<NamedBuildingScreenLayout 
						namedBuildings={NamedBuildingList}
						currentIndex={currentIndex}
						totalPages={namedBuildingListTotalPages} />
				</div>);

		case ss_CAMPAIGN_SPLASH:
			if (currentCampaignInfo === null) return null;
			return (
				<div className="top-container-root">
					<CampaignInfoScreen 
						title={currentCampaignInfo.title}
						info={currentCampaignInfo.info}
						image={currentCampaignInfo.image.full.url}
						narrow={false}
						mirrored={false} />	
				</div>
			);
			

		case ss_DONOR_CARDS:
			return (
				<div className="top-container-root">
					{thereAreNoCards 
						? <p style={errorTextStyle}>There are no results.</p>
						: <DonorCardLayout 
							donors={cardsList} 
							currentIndex={currentIndex} 
							totalPages={totalPages}
							isTentative={pageTotalIsTentative} />}
				</div>);

		default: return new Error("Invalid screen state"); // Should never happen.
	}
}


const errorTextStyle = {
	fontFamily: 'Arial',
	color: 'white',
	fontSize: 16
};

export default SplashScreen
